import React from 'react';
import { Box, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  REGION_COLORS,
  REGION_LABELS,
  formatDateTime,
  formatPrice,
  useContainerWidth,
  CHART_MARGINS_SIMPLE,
  defaultLineLegendProps,
  rotatedTickLabelStyle,
  tickLabelStyle,
} from './shared';

/**
 * Multi-line chart of regional average spot prices ($/MWh) over time.
 */
const PriceLineChart = ({ price, frequency }) => {
  const { ref, width } = useContainerWidth();

  if (!price || price.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">No price data available for this period</Typography>
      </Box>
    );
  }

  const buckets = {};
  const regionIds = new Set();
  price.forEach(({ date_time, region_id, avg_price }) => {
    const key = String(date_time);
    if (!buckets[key]) buckets[key] = {};
    buckets[key][region_id] = avg_price;
    regionIds.add(region_id);
  });

  const sortedBuckets = Object.keys(buckets).sort();
  const sortedRegions = Array.from(regionIds).sort();
  const xLabels = sortedBuckets.map((b) => formatDateTime(b, frequency));

  const series = sortedRegions.map((rid) => ({
    type: 'line',
    label: REGION_LABELS[rid] || rid,
    data: sortedBuckets.map((b) => buckets[b][rid] ?? null),
    color: REGION_COLORS[rid] || '#6b7280',
    curve: 'monotone',
    connectNulls: true,
  }));

  return (
    <Box ref={ref} sx={{ width: '100%', height: 380, minWidth: 0, overflow: 'hidden' }}>
      {width > 0 && (
        <LineChart
          width={width}
          height={380}
          series={series}
          xAxis={[{
            scaleType: 'point',
            data: xLabels,
            tickLabelStyle: rotatedTickLabelStyle,
            tickNumber: xLabels.length > 30 ? 30 : undefined,
          }]}
          yAxis={[{ valueFormatter: formatPrice, tickLabelStyle }]}
          margin={CHART_MARGINS_SIMPLE}
          slotProps={{ legend: defaultLineLegendProps }}
        />
      )}
    </Box>
  );
};

export default PriceLineChart;
